/**
 * 消息内附件区（T09）。
 *
 * 给定 Attachment[]：图片渲染成可点开的大图缩略，其它渲染成可下载 / 另存的文件卡片；
 * 大图预览是一个带遮罩的弹层（动效走 `src/design/motion.ts`）。
 * 每个可见部件都登记进 `src/ui/registry.tsx`，坐标一律用 390x844 画布坐标声明。
 */

import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import type { ReactElement } from 'react';

import type { Attachment } from '../../core/types';
import { useMotionPresets } from '../../design/motion';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { BoxLayout, ComponentMetaInput } from '../../ui';
import styles from './attachments.module.css';
import { contentBoxStyle, listFileLayout, listImageLayout, offsetBoxStyle } from './layout';
import { formatFileSize } from './validate';

export interface AttachmentListProps {
  attachments: readonly Attachment[];
  /** 附件区左上角（画布坐标）。*/
  layout: BoxLayout;
  /** 组件清单里的页面标识。*/
  page?: string;
}

/** 大图预览弹层（画布坐标）。*/
const BACKDROP: BoxLayout = { x: 0, y: 24, width: 390, height: 744 };
const PREVIEW: BoxLayout = { x: 16, y: 104, width: 358, height: 552 };
const PREVIEW_CLOSE: BoxLayout = { x: 330, y: 112, width: 44, height: 44 };

interface ImageTileProps {
  attachment: Attachment;
  layout: BoxLayout;
  parentLayout: BoxLayout;
  page: string;
  onOpen: (attachment: Attachment) => void;
}

function ImageTile({ attachment, layout, parentLayout, page, onOpen }: ImageTileProps): ReactElement {
  const presets = useMotionPresets();
  const meta: ComponentMetaInput = {
    id: `attachments.list.image.${attachment.id}`,
    page,
    role: 'button',
    label: `查看大图：${attachment.name}`,
    layout,
    zIndex: 5,
    notes: '点开大图预览；安卓用 ImageButton + Dialog',
  };
  const ref = useComponentMeta<HTMLButtonElement>(buildComponentMeta(meta, 'button'));

  return (
    <motion.button
      ref={ref}
      type="button"
      className={styles.imageTile}
      data-ui-id={meta.id}
      data-ui-role="button"
      aria-label={meta.label}
      style={offsetBoxStyle(layout, parentLayout)}
      whileTap={presets.tap.whileTap}
      transition={presets.tap.transition}
      onClick={() => {
        onOpen(attachment);
      }}
    >
      {attachment.dataUrl !== undefined ? (
        <img className={styles.imageThumb} src={attachment.dataUrl} alt={attachment.name} />
      ) : (
        <span className={styles.fileName}>{attachment.name}</span>
      )}
    </motion.button>
  );
}

interface FileCardProps {
  attachment: Attachment;
  layout: BoxLayout;
  parentLayout: BoxLayout;
  page: string;
}

function FileCard({ attachment, layout, parentLayout, page }: FileCardProps): ReactElement {
  const downloadable = attachment.dataUrl !== undefined;
  const meta: ComponentMetaInput = {
    id: `attachments.list.file.${attachment.id}`,
    page,
    role: downloadable ? 'button' : 'container',
    label: `文件：${attachment.name}`,
    layout,
    zIndex: 5,
    props: ['download'],
    notes: '文件卡片：可下载 / 另存；安卓用 MaterialCardView + ACTION_CREATE_DOCUMENT',
  };
  const ref = useComponentMeta<HTMLAnchorElement>(buildComponentMeta(meta, 'container'));

  return (
    <a
      ref={ref}
      className={styles.fileCard}
      data-ui-id={meta.id}
      data-ui-role={downloadable ? 'button' : 'container'}
      aria-label={meta.label}
      style={offsetBoxStyle(layout, parentLayout)}
      {...(downloadable ? { href: attachment.dataUrl, download: attachment.name } : {})}
    >
      <span className={styles.fileIcon} aria-hidden="true">
        📄
      </span>
      <span className={styles.fileMeta}>
        <span className={styles.fileName}>{attachment.name}</span>
        <span className={styles.fileSize}>{formatFileSize(attachment.size)}</span>
      </span>
    </a>
  );
}

interface PreviewOverlayProps {
  attachment: Attachment;
  page: string;
  onClose: () => void;
}

function PreviewOverlay({ attachment, page, onClose }: PreviewOverlayProps): ReactElement {
  const presets = useMotionPresets();
  const backdropMeta: ComponentMetaInput = {
    id: 'attachments.preview.backdrop',
    page,
    role: 'modal',
    label: '大图预览遮罩',
    layout: BACKDROP,
    zIndex: 40,
    notes: '点击遮罩关闭；安卓用 Dialog + 全屏遮罩',
  };
  const backdropRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(backdropMeta, 'modal'));

  const imageMeta: ComponentMetaInput = {
    id: 'attachments.preview.image',
    page,
    role: 'icon',
    label: `大图：${attachment.name}`,
    layout: PREVIEW,
    zIndex: 41,
    notes: '按 contain 适配；安卓用 ImageView + adjustViewBounds',
  };
  const imageRef = useComponentMeta<HTMLImageElement>(buildComponentMeta(imageMeta, 'icon'));

  const closeMeta: ComponentMetaInput = {
    id: 'attachments.preview.close',
    page,
    role: 'button',
    label: '关闭大图预览',
    layout: PREVIEW_CLOSE,
    zIndex: 42,
    notes: '触摸目标 44x44dp（≥44dp）',
  };
  const closeRef = useComponentMeta<HTMLButtonElement>(buildComponentMeta(closeMeta, 'button'));

  return (
    <motion.div
      ref={backdropRef}
      className={styles.backdrop}
      data-ui-id={backdropMeta.id}
      data-ui-role="modal"
      role="dialog"
      aria-modal="true"
      aria-label={backdropMeta.label}
      style={contentBoxStyle(BACKDROP)}
      variants={presets.sheetBackdrop}
      initial="hidden"
      animate="visible"
      exit="hidden"
      onClick={onClose}
    >
      {attachment.dataUrl !== undefined ? (
        <img
          ref={imageRef}
          className={styles.previewImage}
          data-ui-id={imageMeta.id}
          data-ui-role="icon"
          alt={attachment.name}
          src={attachment.dataUrl}
          style={offsetBoxStyle(PREVIEW, BACKDROP)}
          onClick={(event) => {
            event.stopPropagation();
          }}
        />
      ) : null}
      <button
        ref={closeRef}
        type="button"
        className={styles.previewClose}
        data-ui-id={closeMeta.id}
        data-ui-role="button"
        aria-label={closeMeta.label}
        style={offsetBoxStyle(PREVIEW_CLOSE, BACKDROP)}
        onClick={onClose}
      >
        ✕
      </button>
    </motion.div>
  );
}

/** 消息内附件区。*/
export function AttachmentList({
  attachments,
  layout,
  page = 'attachments',
}: AttachmentListProps): ReactElement {
  const [preview, setPreview] = useState<Attachment | null>(null);
  const images = attachments.filter((item) => item.kind === 'image');
  const files = attachments.filter((item) => item.kind !== 'image');

  const containerMeta: ComponentMetaInput = {
    id: 'attachments.list',
    page,
    role: 'list',
    label: '消息内附件区',
    layout,
    zIndex: 4,
    notes: '图片可点开大图；文件可下载 / 另存',
  };
  const containerRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(containerMeta, 'list'));

  return (
    <>
      <div
        ref={containerRef}
        className={styles.attachmentArea}
        data-ui-id={containerMeta.id}
        data-ui-role="list"
        role="list"
        style={contentBoxStyle(layout)}
      >
        {images.map((attachment, index) => (
          <ImageTile
            key={attachment.id}
            attachment={attachment}
            layout={listImageLayout(layout, index)}
            parentLayout={layout}
            page={page}
            onOpen={setPreview}
          />
        ))}
        {files.map((attachment, index) => (
          <FileCard
            key={attachment.id}
            attachment={attachment}
            layout={listFileLayout(layout, index, images.length)}
            parentLayout={layout}
            page={page}
          />
        ))}
      </div>
      <AnimatePresence>
        {preview !== null ? (
          <PreviewOverlay
            attachment={preview}
            page={page}
            onClose={() => {
              setPreview(null);
            }}
          />
        ) : null}
      </AnimatePresence>
    </>
  );
}